import React, { useEffect, useRef, useState } from 'react';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@rocco/components/accordion';
import type { FilterStates } from '@rocco/ui/filter-sort/view-models/filter-states';
import type { ModalFiltersConfigSection } from '@rocco/ui/filter-sort/types/filter-config-section';
import {
    isLocationFilterState,
    isMultipleValuesFilterState,
    isRangeFilterState,
    isSearchableListFilterState,
    isSingleValueFilterState,
} from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import { isFilterStateActive } from '@rocco/ui/filter-sort/components/filters-modal/FiltersModal';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import { useFiltersContext } from '@rocco/ui/filter-sort/components/Filters';

interface FiltersModalSectionProps extends React.PropsWithChildren {
    sectionConfig: ModalFiltersConfigSection;
    filterStates: FilterStates;
    displayMode: DisplayMode;
}

export const FiltersModalSection = (props: FiltersModalSectionProps) => {
    const { children, sectionConfig, filterStates, displayMode } = props;

    const {
        title,
        collapsedByDefault = false,
        collapsable = true,
        columns,
    } = sectionConfig;

    const openedItemRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<number | null>(null);

    const [isOpen, setIsOpen] = useState(!collapsedByDefault);
    const { onSectionOpen } = useFiltersContext();

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                cancelAnimationFrame(timeoutRef.current);
            }
        };
    }, []);

    if (isSectionVisible(sectionConfig, filterStates)) {
        const containerStyles =
            displayMode === DisplayMode.Desktop && columns && columns > 1
                ? { gridTemplateColumns: `repeat(${columns}, 1fr)` }
                : {};

        const hasActiveFilters = isSectionHasActiveFilters(
            sectionConfig,
            filterStates,
        );

        const handleValueChange = (value: string) => {
            setIsOpen(!isOpen);

            if (value) {
                onSectionOpen?.(title);
                timeoutRef.current = requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        // iOS dismisses the keyboard when a focused field's
                        // ancestor is scrolled into view.
                        const active = document.activeElement;
                        if (
                            active instanceof HTMLElement &&
                            (active.tagName === 'INPUT' ||
                                active.tagName === 'TEXTAREA' ||
                                active.isContentEditable)
                        ) {
                            return;
                        }
                        if (openedItemRef.current) {
                            openedItemRef.current.scrollIntoView({
                                behavior: 'smooth',
                            });
                        }
                    });
                });
            } else if (timeoutRef.current) {
                cancelAnimationFrame(timeoutRef.current);
            }
        };

        return (
            <div className="flex flex-col gap-2">
                <Accordion
                    type="single"
                    collapsible={collapsable}
                    defaultValue={collapsedByDefault ? undefined : title}
                    onValueChange={handleValueChange}
                >
                    <AccordionItem value={title}>
                        <div className="px-6 md:px-0" ref={openedItemRef}>
                            <AccordionTrigger
                                variant="spacious"
                                icon="plus"
                                openIcon="minus"
                                isOpen={isOpen}
                            >
                                <div className="flex w-full items-center justify-between">
                                    <span>{title}</span>
                                    {hasActiveFilters && (
                                        <div className="mr-2 size-4 rounded-full bg-brick-800" />
                                    )}
                                </div>
                            </AccordionTrigger>
                            <AccordionContent>
                                <div
                                    className="grid py-2"
                                    style={containerStyles}
                                >
                                    {children}
                                </div>
                            </AccordionContent>
                        </div>
                    </AccordionItem>
                </Accordion>
            </div>
        );
    }

    return null;
};

/**
 * Determines if a filter section should be visible based on its filters' states.
 * @param filterSection - The configuration for the filter section.
 * @param filterStates - The current states of all filters.
 * @returns True if any filter in the section has a non-empty state, false otherwise.
 **/
export const isSectionVisible = (
    filterSection: ModalFiltersConfigSection,
    filterStates: FilterStates,
): boolean => {
    return filterSection.filters.some((filterConfigItem) => {
        const filterState =
            filterStates.states[filterConfigItem.filterKey] ??
            filterStates.facet?.facetOptions?.find(
                (facet) => facet.id === filterConfigItem.filterKey,
            )?.state ??
            null;

        if (filterState) {
            if (
                isSingleValueFilterState(filterState) ||
                isLocationFilterState(filterState)
            ) {
                return true;
            }

            if (isSearchableListFilterState(filterState)) {
                return (
                    filterState.items.length > 0 ||
                    filterState.selectedItems.length > 0 ||
                    filterState.search.length > 0 ||
                    !!filterState.isSearching
                );
            }

            if (isMultipleValuesFilterState(filterState)) {
                return (
                    filterState.items.length > 0 ||
                    filterState.selectedItems.length > 0
                );
            }

            if (isRangeFilterState(filterState)) {
                const { min, max } = filterState.range;

                return (min !== 0 || max !== 0) && min < max;
            }
        }

        return false;
    });
};

const isSectionHasActiveFilters = (
    filterSection: ModalFiltersConfigSection,
    filterStates: FilterStates,
): boolean => {
    return filterSection.filters.some((filterConfigItem) => {
        const filterState =
            filterStates.states[filterConfigItem.filterKey] ??
            filterStates.facet?.facetOptions?.find(
                (facet) => facet.id === filterConfigItem.filterKey,
            )?.state ??
            null;
        return filterState && isFilterStateActive(filterState);
    });
};
